import { motion } from 'motion/react'
import { useSwipeToDismiss } from '../hooks/useSwipeToDismiss'
import { Sheet, SheetContent, SheetTitle, SheetDescription } from './sheet'
import { cn } from '../utils/cn'

interface BottomSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  /** Show the small drag indicator at the top. Default: true */
  showDragHandle?: boolean
  /** Allow swipe-down to dismiss. Default: true (set false while a recording is active, etc.) */
  swipeEnabled?: boolean
  /** Hide shadcn's built-in close button (most bottom sheets use their own). Default: true */
  hideCloseButton?: boolean
  /** Required for a11y. Hidden visually if you don't want a header. */
  title: string
  description?: string
  /** Show or hide the title visually. When false, still rendered for screen readers. Default: false */
  showTitle?: boolean
  className?: string
}

export function BottomSheet({
  open,
  onOpenChange,
  children,
  showDragHandle = true,
  swipeEnabled = true,
  hideCloseButton = true,
  title,
  description,
  showTitle = false,
  className,
}: BottomSheetProps) {
  const { sheetProps } = useSwipeToDismiss({
    onClose: () => onOpenChange(false),
    enabled: swipeEnabled && open,
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={!hideCloseButton}
        className={cn('rounded-t-3xl p-0', className)}
      >
        <motion.div
          {...sheetProps}
          className="flex h-full flex-col"
        >
          {showDragHandle && (
            <div className="mx-auto mt-3 mb-1 h-1 w-12 shrink-0 rounded-full bg-pearl/30" />
          )}
          {showTitle ? (
            <SheetTitle className="px-5 pt-3 pb-1">{title}</SheetTitle>
          ) : (
            <SheetTitle className="sr-only">{title}</SheetTitle>
          )}
          {description && (
            <SheetDescription className={showTitle ? 'px-5' : 'sr-only'}>
              {description}
            </SheetDescription>
          )}
          {children}
        </motion.div>
      </SheetContent>
    </Sheet>
  )
}
